<template>
	<div class="add-visit-details d-flex flex-column">
		<!-- header -->
		<div class="d-flex align-center justify-space-between px-4 py-3 add-visit-details__header">
			<div class="d-flex align-center" @click="cancel">
				<v-icon class="me-2">{{ backIcon }}</v-icon>
				<span class="text-subtitle-1 font-weight-medium">{{ headerTitle }}</span>
			</div>

			<v-menu v-if="isExistingVisit" offset-y>
				<template v-slot:activator="{ on, attrs }">
					<v-btn icon v-bind="attrs" v-on="on" @click.stop>
						<v-icon>mdi-dots-vertical</v-icon>
					</v-btn>
				</template>
				<v-list dense>
					<v-list-item @click="isReadonly = false">
						<v-list-item-title>{{ $T('edit') }}</v-list-item-title>
					</v-list-item>
					<v-list-item @click="confirmDeleteDialog = true">
						<v-list-item-title class="red--text">{{ $T('delete') }}</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-menu>
		</div>

		<div class="px-4">
			<!-- last visit link -->
			<div class="text-start my-3">
				<a class="text-decoration-none text-body-2 primary--text" @click.prevent="viewLastVisit">
					{{ $T('Visits.view-last-visit') }}
				</a>
			</div>

			<!-- customer summary card -->
			<v-card outlined rounded="lg" class="mb-4 add-visit-details__customer-card">
				<div class="d-flex align-stretch">
					<!-- الصورة ملاصقة لحافة البطاقة: تأخذ تدويرها من الجهة البادئة فقط -->
					<v-img
						:src="entityImageSrc"
						width="88"
						aspect-ratio="1"
						class="add-visit-details__entity-image"
					/>
					<div class="flex-grow-1 px-3 py-2 add-visit-details__customer-info">
						<div class="d-flex align-center mb-1 add-visit-details__change-customer" @click="openChangeCustomerSheet">
							<span class="text-subtitle-1 font-weight-bold text--primary me-1 add-visit-details__customer-name">{{ entityDisplayName }}</span>
							<v-icon size="16" color="grey darken-1" class="me-2 flex-shrink-0">mdi-chevron-down</v-icon>
							<!-- static placeholder: no debt/balance-per-entity field exists in the API yet -->
							<v-chip x-small outlined color="red darken-1" class="font-weight-medium flex-shrink-0">
								{{ $T('Visits.debtor') }}
							</v-chip>
						</div>
						<div v-if="entityAddress || entityRegionName" class="text-caption grey--text text--darken-1 mb-1">
							<v-icon size="14" class="me-1" color="grey darken-1">mdi-map-marker</v-icon>
							{{ [entityRegionName, entityAddress].filter(Boolean).join(' - ') }}
						</div>
						<div class="text-caption grey--text text--darken-1">
							<v-icon size="14" class="me-1" :color="isHoliday === 'error' ? 'red darken-1' : 'grey darken-1'">mdi-calendar</v-icon>
							{{ visitDateDisplay }}
							<span v-if="isHoliday === 'error'" class="red--text text--darken-1">(Holiday)</span>
						</div>
					</div>
				</div>

				<v-alert
					v-if="useVisitLocationCheck && locationValid === false"
					type="warning"
					border="left"
					dense
					class="mb-0 mx-4 mb-3"
				>
					{{ locationError || 'Location check failed. Please make sure you are at the customer location.' }}
				</v-alert>
			</v-card>
		</div>

		<v-form ref="form" class="d-flex flex-column flex-grow-1">
				<div class="add-visit-details__tabs d-flex flex-shrink-0">
					<button
						v-for="tab in tabs"
						:key="tab.value"
						type="button"
						class="add-visit-details__tab mx-0"
						:class="activeTab === tab.value ? 'add-visit-details__tab--active primary white--text' : ''"
						@click="activeTab = tab.value"
					>
						<span>{{ tab.label }}</span>
						<span
							v-if="tab.count"
							class="add-visit-details__tab-badge"
							:class="activeTab === tab.value ? 'white primary--text' : 'primary white--text'"
						>
							{{ tab.count }}
						</span>
					</button>
				</div>

				<div class="add-visit-details__panel flex-grow-1">
				<v-tabs-items v-model="activeTab">
					<!-- invoice -->
					<v-tab-item>
						<visit-material-rows-table
							v-model="invoiceItems"
							:columns="invoiceColumns"
							:materials="materials"
							:disabled="isReadonly"
							:allow-decimal="realNumberAmount"
							:get-material-price="getMaterialPrice"
							:get-full-material-name="getFullMaterialName"
							:get-amount-rules="getAmountRules"
						/>
					</v-tab-item>

					<!-- return: sent as materialAmounts entries with returnedTransaction: true and state: the picked reason -->
					<v-tab-item>
						<visit-material-rows-table
							v-model="returnItems"
							:columns="returnColumns"
							:materials="allMaterials"
							:reasons="returnReasons"
							:disabled="isReadonly"
							:allow-decimal="realNumberAmount"
							:get-material-price="getMaterialPrice"
							:get-full-material-name="getFullMaterialName"
						/>
					</v-tab-item>

					<!-- order -->
					<v-tab-item>
						<div>
							<visit-material-rows-table
								v-model="orderItems"
								:columns="['material', 'quantity']"
								:materials="allMaterials"
								:disabled="isReadonly"
								:allow-decimal="realNumberAmount"
								:get-full-material-name="getFullMaterialName"
							/>

							<div class="px-4">
								<div class="text-body-2 mb-1 mt-4">
									<required-label :text="$T('inputs.delivery-date')" />
								</div>
								<v-menu
									v-model="orderDeliveryDateMenu"
									:close-on-content-click="false"
									offset-y
									:disabled="isReadonly"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="orderDeliveryDate"
											readonly
											outlined
											dense
											hide-details="auto"
											:disabled="isReadonly"
											:rules="filledOrderItems.length > 0 ? rules.required : []"
											v-bind="attrs"
											v-on="on"
										/>
									</template>
									<v-date-picker
										v-model="orderDeliveryDate"
										:min="visitDate || moment().format('YYYY-MM-DD')"
										@input="orderDeliveryDateMenu = false"
									/>
								</v-menu>

								<div class="text-body-2 mb-1 mt-4">{{ $T('inputs.delivery-time') }}</div>
								<v-menu
									v-model="orderDeliveryTimeMenu"
									:close-on-content-click="false"
									offset-y
									:disabled="isReadonly"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="orderDeliveryTime"
											readonly
											outlined
											dense
											hide-details="auto"
											:disabled="isReadonly"
											v-bind="attrs"
											v-on="on"
										/>
									</template>
									<v-time-picker
										v-model="orderDeliveryTime"
										format="24hr"
										@click:minute="orderDeliveryTimeMenu = false"
									/>
								</v-menu>

								<div class="text-body-2 mb-1 mt-4">{{ $T('inputs.delivery-place') }}</div>
								<v-text-field
									v-model="orderDeliveryPlace"
									outlined
									dense
									hide-details="auto"
									:disabled="isReadonly"
								/>
							</div>
						</div>
					</v-tab-item>

					<!-- marketing -->
					<v-tab-item>
						<v-row dense class="pt-4 px-4 pb-16">
							<v-col cols="12">
								<div class="text-body-2 mb-1">{{ $T('inputs.visit-notes') }}</div>
								<v-text-field v-model="visitNotes" outlined dense hide-details="auto" :disabled="isReadonly" />
							</v-col>

							<v-col cols="12">
								<div class="text-body-2 mb-1">{{ $T('inputs.core-message') }}</div>
								<v-text-field v-model="coreMessage" outlined dense hide-details="auto" :disabled="isReadonly" />
							</v-col>

							<v-col cols="12">
								<div class="text-body-2 mb-1">{{ $T('inputs.notes') }}</div>
								<v-textarea v-model="centerNotes" outlined dense hide-details rows="3" :disabled="isReadonly" />
							</v-col>

							<v-col cols="12">
								<div class="text-body-2 mb-1">{{ $T('inputs.objection') }}</div>
								<v-text-field v-model="objection" outlined dense hide-details="auto" :disabled="isReadonly" />
							</v-col>

							<v-col cols="12">
								<div class="text-body-2 mb-1">{{ $T('inputs.next-call') }}</div>
								<v-text-field v-model="nextCall" outlined dense hide-details="auto" :disabled="isReadonly" />
							</v-col>

							<v-col cols="12">
								<div class="text-body-2 mb-1">{{ $T('inputs.unavailable-products') }}</div>
								<v-autocomplete
									v-model="unavailableMaterialIds"
									:items="allMaterials"
									:item-text="getFullMaterialName"
									item-value="id"
									outlined
									dense
									multiple
									hide-details="auto"
									:disabled="isReadonly"
								/>
							</v-col>

							<v-col cols="12">
								<div class="text-body-2 mb-1">{{ $T('inputs.duration-minutes') }}</div>
								<v-text-field
									v-model.number="durationInMinutes"
									type="number"
									min="0"
									outlined
									dense
									hide-spin-buttons
									:disabled="isReadonly"
									:suffix="$T('inputs.minutes')"
									hide-details
								/>
							</v-col>

							<v-col cols="12">
								<div class="d-flex justify-space-between align-center mb-1">
									<span class="text-body-2">{{ $T('inputs.persons') }}</span>
									<v-btn v-if="!isReadonly" icon small @click="addPerson">
										<v-icon color="grey darken-3">mdi-plus-circle-outline</v-icon>
									</v-btn>
								</div>
								<div v-if="isReadonly && !personsNames.length" class="text-body-2 grey--text">
									{{ $T('no-data') }}
								</div>
								<v-row v-for="(person, index) in personsNames" :key="person.id" dense class="align-center">
									<v-col cols="10">
										<v-text-field v-model="person.name" outlined dense hide-details="auto" :disabled="isReadonly" />
									</v-col>
									<v-col v-if="!isReadonly" cols="2" class="pa-0">
										<v-btn icon small @click="removePerson(index)">
											<v-icon color="grey darken-3">mdi-minus-circle-outline</v-icon>
										</v-btn>
									</v-col>
								</v-row>
							</v-col>

							<v-col cols="12">
								<div class="d-flex justify-space-between align-center mb-1">
									<span class="text-body-2">{{ $T('inputs.competitors') }}</span>
									<v-btn v-if="!isReadonly" icon small @click="addCompetitor">
										<v-icon color="grey darken-3">mdi-plus-circle-outline</v-icon>
									</v-btn>
								</div>
								<div v-if="isReadonly && !competitors.length" class="text-body-2 grey--text">
									{{ $T('no-data') }}
								</div>
								<v-row v-for="(competitor, index) in competitors" :key="competitor.id" dense class="align-center">
									<v-col cols="10">
										<v-text-field v-model="competitor.value" outlined dense hide-details="auto" :disabled="isReadonly" />
									</v-col>
									<v-col v-if="!isReadonly" cols="2" class="pa-0">
										<v-btn icon small @click="removeCompetitor(index)">
											<v-icon color="grey darken-3">mdi-minus-circle-outline</v-icon>
										</v-btn>
									</v-col>
								</v-row>
							</v-col>

							<v-col cols="12">
								<div class="text-body-2 mb-1">{{ $T('inputs.other-materials') }}</div>
								<v-text-field v-model="otherSamples" outlined dense hide-details :disabled="isReadonly" />
							</v-col>
						</v-row>
					</v-tab-item>
				</v-tabs-items>
				</div>
		</v-form>

		<!-- footer -->
		<v-footer app fixed padless class="add-visit-details__footer">
		<v-card flat width="100%" color="white">
			<v-card-text v-if="usePrices" class="pb-0 pt-2">
				<div class="d-flex justify-space-between text-body-2 py-2">
					<span>{{ $T('Visits.materials-total') }}</span>
					<span>{{ $options.filters.formatNumber(materialsTotal) }} {{ $T('currency') }}</span>
				</div>
				<div class="d-flex justify-space-between text-body-2 py-2">
					<span>{{ $T('Visits.discount') }}</span>
					<span>{{ $options.filters.formatNumber(discountAmount) }} {{ $T('currency') }}</span>
				</div>
				<div class="d-flex justify-space-between text-body-2 py-2">
					<span>{{ $T('Visits.returns-total') }}</span>
					<span class="red--text">{{ $options.filters.formatNumber(returnsTotal) }} {{ $T('currency') }}</span>
				</div>
				<v-divider />
				<div class="d-flex justify-space-between text-subtitle-1 font-weight-bold py-2">
					<span>{{ $T('Visits.final-total') }}</span>
					<span class="green--text text--darken-1">{{ $options.filters.formatNumber(finalTotal) }} {{ $T('currency') }}</span>
				</div>
			</v-card-text>

			<!-- viewing an existing visit: no wizard step to advance to, so the actions
				become the invoice/payment shortcuts instead of cancel + next -->
			<v-card-actions v-if="isReadonly" class="add-visit-details__actions">
				<template v-if="usePrices">
					<v-btn
						depressed
						rounded
						x-large
						color="orange lighten-5"
						class="add-visit-details__primary-btn text-none orange--text text--darken-2"
						@click="previewInvoice"
					>
						<v-icon size="18" class="me-1">mdi-receipt-text-outline</v-icon>
						{{ $T('Visits.view-invoice') }}
					</v-btn>
					<v-btn
						depressed
						rounded
						x-large
						color="grey lighten-3"
						class="add-visit-details__secondary-btn text-none grey--text text--darken-3"
						@click="openPaymentDetails"
					>
						<v-icon size="18" class="me-1">mdi-credit-card-outline</v-icon>
						{{ $T('Visits.payment-details') }}
					</v-btn>
				</template>
				<v-btn v-else outlined rounded x-large class="add-visit-details__primary-btn" @click="cancel">
					{{ $T('close') }}
				</v-btn>
			</v-card-actions>

			<v-card-actions v-else class="add-visit-details__actions">
				<v-btn outlined rounded x-large class="add-visit-details__secondary-btn" @click="cancel">
					{{ $T('cancel') }}
				</v-btn>
				<v-btn
					color="primary"
					rounded
					x-large
					class="add-visit-details__primary-btn"
					:disabled="!canProceed"
					@click="next"
				>
					{{ $T('next') }}
				</v-btn>
			</v-card-actions>

			<v-progress-linear v-if="!isReadonly" :value="66" height="3" color="primary" />
		</v-card>
		</v-footer>

		<!-- change customer mid-flow: same picker as the initial bottom sheet, but only updates the customer -->
		<add-visit-bottom-sheet ref="ChangeCustomerSheet" skip-reset @submit="onCustomerChanged" />

		<!-- delete confirmation, reached from the options menu when viewing an existing visit -->
		<v-dialog v-model="confirmDeleteDialog" max-width="400">
			<v-card>
				<v-card-text
					class="pt-4"
					v-html="$T('messages.are-you-sure-you-want-to-delete-$', { text: $T('Visits.the-visit') })"
				/>
				<v-card-actions>
					<v-spacer/>
					<v-btn text color="grey darken-1" :disabled="deleteLoading" @click="confirmDeleteDialog = false">
						{{ $T('cancel') }}
					</v-btn>
					<v-btn text color="red lighten-1" :loading="deleteLoading" :disabled="deleteLoading" @click="deleteVisit">
						{{ $T('delete') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- read-only payment breakdown, reached from the footer while viewing an existing visit -->
		<v-bottom-sheet v-model="paymentDetailsSheet" content-class="rounded-t-lg">
			<v-card class="rounded-t-lg">
				<div class="d-flex align-center justify-space-between px-4 py-3">
					<span class="text-subtitle-1 font-weight-medium">{{ $T('Visits.payment-details') }}</span>
					<v-btn icon small @click="paymentDetailsSheet = false">
						<v-icon size="20">mdi-close</v-icon>
					</v-btn>
				</div>

				<v-divider/>

				<v-card-text class="px-4 py-4">
					<div v-if="!paymentRows.length" class="text-body-2 grey--text text-center py-4">
						{{ $T('no-data') }}
					</div>

					<div
						v-for="row in paymentRows"
						:key="row.id"
						class="d-flex justify-space-between text-body-2 py-2"
					>
						<span>{{ row.name }}</span>
						<span class="font-weight-medium">
							{{ $options.filters.formatNumber(row.amount) }} {{ $T('currency') }}
						</span>
					</div>

					<v-divider class="my-2"/>

					<div class="d-flex justify-space-between text-body-2 py-2">
						<span>{{ $T('Visits.discount') }}</span>
						<span>{{ $options.filters.formatNumber(discountAmount) }} {{ $T('currency') }}</span>
					</div>

					<div class="d-flex justify-space-between text-subtitle-2 font-weight-bold py-2">
						<span>{{ $T('Visits.final-total') }}</span>
						<span class="green--text text--darken-1">
							{{ $options.filters.formatNumber(finalTotal) }} {{ $T('currency') }}
						</span>
					</div>
				</v-card-text>
			</v-card>
		</v-bottom-sheet>

		<!-- last visit: date + delivered samples for the currently selected customer -->
		<v-bottom-sheet v-model="lastVisitSheet" content-class="rounded-t-lg">
			<v-card class="rounded-t-lg">
				<div class="d-flex align-center justify-space-between px-4 py-3">
					<span class="text-subtitle-1 font-weight-medium">{{ $T('Visits.view-last-visit') }}</span>
					<v-btn icon small @click="lastVisitSheet = false">
						<v-icon size="20">mdi-close</v-icon>
					</v-btn>
				</div>

				<v-divider/>

				<v-card-text class="px-4 py-4">
					<div v-if="lastVisitLoading" class="d-flex justify-center py-8">
						<v-progress-circular indeterminate color="primary"/>
					</div>

					<template v-else-if="lastVisit">
						<div class="d-flex align-center text-caption grey--text text--darken-1 mb-4">
							<v-icon size="14" class="me-1" color="grey darken-1">mdi-calendar</v-icon>
							{{ lastVisitDate }}
						</div>

						<div class="text-subtitle-2 font-weight-bold mb-2">{{ $T('inputs.samples') }}</div>

						<v-card v-if="lastVisitSampleRows.length" outlined rounded="lg">
							<v-simple-table dense>
								<tbody>
									<tr v-for="row in lastVisitSampleRows" :key="row.materialId">
										<td class="text-body-2">{{ row.name }}</td>
										<td class="text-end">
											<v-chip small color="grey lighten-4" text-color="primary" class="font-weight-bold">
												×{{ row.amount }}
											</v-chip>
										</td>
									</tr>
								</tbody>
							</v-simple-table>
						</v-card>

						<div v-else class="text-body-2 grey--text text-center py-4">
							{{ $T('Visits.no-samples-delivered') }}
						</div>
					</template>

					<div v-else class="text-body-2 grey--text text-center py-8">
						{{ $T('Visits.no-last-visit') }}
					</div>
				</v-card-text>
			</v-card>
		</v-bottom-sheet>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import rules from '@/helpers/validation rules'
import { getDateColor } from '@/helpers/functions'
import { isOffline } from '@/helpers/offline-simulation'
import { paymentMethod, transactionState } from '@/helpers/enums'
import { usePrices, enableVacations, useVisitOrderRequest, useVisitLocationCheck } from '@/configs/general.configs'
import RequiredLabel from '@/components/RequiredLabel.vue'
import VisitMaterialRowsTable from '@/components/VisitMaterialRowsTable.vue'
import AddVisitBottomSheet from '@/components/AddVisitBottomSheet.vue'

export default {
	name: 'AddVisitDetails',

	components: {
		RequiredLabel,
		VisitMaterialRowsTable,
		AddVisitBottomSheet
	},

	data() {
		return {
			usePrices,
			useVisitLocationCheck,
			rules,
			moment,
			transactionState,

			activeTab: 0,

			customerTypeId: null,
			entityId: null,
			entityImages: null,
			visitDate: null,
			priceTypeId: null,
			durationInMinutes: null,

			invoiceItems: [],
			returnItems: [],
			orderItems: [],
			originalMaterialAmounts: {},
			orderDeliveryDate: null,
			orderDeliveryDateMenu: false,
			orderDeliveryTime: null,
			orderDeliveryTimeMenu: false,
			orderDeliveryPlace: null,

			visitNotes: null,
			coreMessage: null,
			centerNotes: null,
			objection: null,
			nextCall: null,
			unavailableMaterialIds: [],
			otherSamples: null,
			personsNames: [],
			competitors: [],
			personCounter: 0,
			competitorCounter: 0,

			isHoliday: null,
			locationChecking: false,
			locationValid: null,
			locationError: null,
			locationPermissionChecked: false,

			// true when opened from a visit card's "Details" button to view an existing visit
			isReadonly: false,
			confirmDeleteDialog: false,
			deleteLoading: false,

			lastVisitSheet: false,
			lastVisitLoading: false,
			lastVisit: null,

			paymentDetailsSheet: false
		}
	},

	computed: {
		...mapState({
			allMaterials: state => state.materials.materials,
			transactions: state => state.materialsTransactions.transactions,
			regions: state => state.regions.regions,
			priceTypes: state => state.priceTypes.priceTypes,
			vacations: state => state.vacations.vacations,
			vacationsDayDetails: state => state.vacations.vacationsDayDetails,
			realNumberAmount: state => state.configurations.realNumberAmount
		}),

		...mapGetters({
			getMaterialById: 'materials/getMaterialById',
			getMaterialFormById: 'materialForms/getMaterialFormById',
			getRegionById: 'regions/getRegionById',
			getVisitById: 'visits/getVisitById',
			getEntityById: 'entities/getEntityById',
			getAdjustedRemainingAmount: 'materialsTransactions/getAdjustedRemainingAmount'
		}),

		isExistingVisit() {
			return Boolean(this.$store.state.visitDraft.id)
		},

		// opened with a visitId (view or edit of an existing visit) vs. creating a new one.
		// keyed off the route rather than the draft so the title is right before hydration finishes
		headerTitle() {
			return this.$route.query.visitId ? this.$T('Visits.visit-details') : this.$T('Visits.add-visit')
		},

		backIcon() {
			return this.$vuetify.rtl ? 'mdi-chevron-right' : 'mdi-chevron-left'
		},

		entity() {
			return this.getEntityById(this.entityId) || null
		},

		/*
			الاسم لا يعتمد على متجر الزبائن وحده: تلك القائمة تُملأ بجلب شامل عند الإقلاع
			(entities/fetchAllGroupedByRegion) قد يكون ما يزال جارياً أو فاشلاً عند دخول
			الصفحة، فيظهر الاسم فارغاً. الاسم المحفوظ في المسودة (من اختيار الزبون أو من
			الزيارة القادمة من الخادم) يغطي هذه الحالة، والمتجر يبقى مصدراً محدَّثاً إن توفّر
		*/
		entityDisplayName() {
			return this.entity?.name || this.$store.state.visitDraft.entityName || ''
		},

		// getEntityById checks state.entities first (richer shape, with `entityAddresses[]`,
		// populated by the add-visit bottom sheet's own entities/fetchAll) then falls back to
		// state.allEntities (a brief shape: flat `address` string, `regionName` string, no
		// `entityAddresses`/`regionId` - populated app-wide by entities/fetchAllGroupedByRegion)
		entityRegionId() {
			return this.entity?.entityAddresses?.[0]?.regionId || null
		},

		entityRegionName() {
			if (!this.entity) return null
			if (this.entityRegionId) return this.getRegionById(this.entityRegionId)?.name || null
			return this.entity.regionName || null
		},

		entityAddress() {
			return this.entity?.entityAddresses?.[0]?.address || this.entity?.address || null
		},

		/*
			صورة الزبون: المجلوبة من السيرفر أولاً لأن getEntityById قد يرجّع النسخة
			المختصرة من allEntities وهي بلا images، وإلا فالصورة البديلة المشتركة
		*/
		entityImageSrc() {
			const images = this.entityImages || this.entity?.images
			const imagePath = images?.find(image => image.isDefault)?.imagePath || images?.[0]?.imagePath
			return imagePath || require('@/assets/imgs/entity-placeholder.svg')
		},

		visitDateDisplay() {
			return this.visitDate ? moment(this.visitDate).format('YYYY/MM/DD') : null
		},

		lastVisitDate() {
			return this.lastVisit ? moment(this.lastVisit.createdAt).format('DD/MM/YYYY') : null
		},

		lastVisitSampleRows() {
			if (!this.lastVisit?.materialAmounts?.length) return []
			return this.lastVisit.materialAmounts
				.filter(materialAmount => !materialAmount.returnedTransaction)
				.map(materialAmount => {
					const material = this.getMaterialById(materialAmount.materialId)
					if (!material) return null
					// unlike getFullMaterialName(), no "remaining" suffix - this is a past visit, not stock to pick from
					const details = [
						this.getMaterialFormById(material.materialFormId)?.name,
						material.dose,
						material.strength
					].filter(Boolean).join(' ')
					return {
						materialId: material.id,
						name: `${material.name}${details ? ` (${details})` : ''}`,
						amount: materialAmount.amount
					}
				})
				.filter(Boolean)
		},

		// every sample is offered on the invoice tab, including the ones with no balance left -
		// the ones that still have stock float to the top, the rest keep the store's name order
		materials() {
			return [...this.allMaterials].sort(
				(a, b) => this.getAdjustedRemainingAmount(b.id) - this.getAdjustedRemainingAmount(a.id)
			)
		},

		returnReasons() {
			return Object.keys(transactionState).map(key => ({
				id: transactionState[key],
				name: this.$T(`Visits.return-reasons.${key}`)
			}))
		},

		invoiceColumns() {
			return this.usePrices ? ['material', 'quantity', 'price', 'total'] : ['material', 'quantity']
		},

		returnColumns() {
			return this.usePrices ? ['material', 'quantity', 'price', 'reason', 'total'] : ['material', 'quantity', 'reason']
		},

		tabs() {
			return [
				{ value: 0, label: this.$T('Visits.tab-invoice'), count: this.filledInvoiceItems.length },
				{ value: 1, label: this.$T('Visits.tab-return'), count: this.filledReturnItems.length },
				{ value: 2, label: this.$T('Visits.order'), count: 0 },
				{ value: 3, label: this.$T('Visits.tab-marketing'), count: 0 }
			]
		},

		filledInvoiceItems() {
			return this.invoiceItems.filter(item => item.data.materialId)
		},

		filledReturnItems() {
			return this.returnItems.filter(item => item.data.materialId)
		},

		filledOrderItems() {
			return this.orderItems.filter(item => item.data.materialId)
		},

		materialsTotal() {
			return this.invoiceItems.reduce((res, { data }) => {
				const priceNet = (Number(data.amount) || 0) * (Number(data.price) || 0)
				return res + (priceNet - ((Number(data.discount) || 0) / 100) * priceNet)
			}, 0)
		},

		returnsTotal() {
			return this.returnItems.reduce((res, { data }) => {
				return res + ((Number(data.amount) || 0) * (Number(data.price) || 0))
			}, 0)
		},

		// entered on the payment step; stays 0 for a visit loaded from the server, which has no discount field
		discountAmount() {
			return Number(this.$store.state.visitDraft.discountAmount) || 0
		},

		finalTotal() {
			return this.materialsTotal - this.returnsTotal - this.discountAmount
		},

		paymentRows() {
			const names = {
				[paymentMethod.cash]: this.$T('payment-method.cash'),
				[paymentMethod.credit]: this.$T('payment-method.credit'),
				[paymentMethod.debit]: this.$T('payment-method.debit')
			}

			return (this.$store.state.visitDraft.payments || [])
				.filter(({ data }) => data.paymentMethodId)
				.map(({ id, data }) => ({
					id,
					name: names[data.paymentMethodId] || '-',
					amount: Number(data.amount) || 0
				}))
		},

		canProceed() {
			return !(this.useVisitLocationCheck && (this.locationChecking || this.locationValid === false))
		}
	},

	watch: {
		entityId() {
			this.entityImages = null
			this.fetchEntityImages()
		}
	},

	methods: {
		fetchEntityImages() {
			if (!this.entityId || isOffline()) return

			this.$store.dispatch('entities/fetchImages', this.entityId)
				.then(images => { this.entityImages = images || [] })
				.catch(() => {})
		},

		getFullMaterialName(material) {
			if (!material) return ''
			const details = [
				this.getMaterialFormById(material.materialFormId)?.name,
				material.dose,
				material.strength,
				`${this.$T('Visits.remaining')}: ${this.$options.filters.formatNumber(this.getAdjustedRemainingAmount(material.id), 3)}`
			]
			return `${material.name} (${details.filter(c => c != null).join(' ')})`
		},

		getMaterialPrice(materialId) {
			if (!materialId) return 0
			const material = this.getMaterialById(materialId)
			const priceObj = (material.prices || []).find(c => c.priceId === this.priceTypeId)
			return (priceObj && priceObj.active ? priceObj.price || material.price : material.price) || 0
		},

		// ported from the old VisitDialog.vue's getMaterialPoolAmount/getMaterialAvailableAmount/
		// checkSampleAmountLimit, adjusted to read the shared adjusted-remaining getter and invoiceItems
		getMaterialPoolAmount(materialId) {
			const base = this.getAdjustedRemainingAmount(materialId)
			if (!this.isExistingVisit) return base
			return base + (this.originalMaterialAmounts[materialId] || 0)
		},

		getMaterialAvailableAmount(materialId, excludeRowId = null) {
			if (!materialId) return 0

			const pool = this.getMaterialPoolAmount(materialId)
			const usedByOtherRows = this.invoiceItems.reduce((sum, row) => {
				if (row.id === excludeRowId || row.data.materialId !== materialId) return sum
				return sum + (Number(row.data.amount) || 0)
			}, 0)

			return pool - usedByOtherRows
		},

		getAmountRules(row) {
			return [(val) => {
				if (!row.data.materialId || val === null || val === '') return true

				const limit = this.getMaterialAvailableAmount(row.data.materialId, row.id)
				return Number(val) <= limit || this.$T('Visits.amount-exceeds-remaining', {
					limit: this.$options.filters.formatNumber(limit, 3)
				})
			}]
		},

		addPerson() {
			this.personsNames.push({ id: this.personCounter++, name: null })
		},

		removePerson(index) {
			this.personsNames.splice(index, 1)
		},

		addCompetitor() {
			this.competitors.push({ id: this.competitorCounter++, value: null })
		},

		removeCompetitor(index) {
			this.competitors.splice(index, 1)
		},

		getHoliday(date) {
			this.isHoliday = getDateColor(date, this.vacations, {
				vacationDayDetails: this.vacationsDayDetails,
				enableVacations
			})
		},

		shouldCheckVisitLocation() {
			return Boolean(useVisitOrderRequest && useVisitLocationCheck)
		},

		ensureLocationPermissionOnPageEnter() {
			if (!this.shouldCheckVisitLocation() || this.locationPermissionChecked) return
			this.locationPermissionChecked = true

			if (!navigator.geolocation) {
				this.locationValid = false
				this.locationError = 'GPS is not available on this device.'
				return
			}

			if (!navigator.permissions || !navigator.permissions.query) {
				this.requestLocationPermissionIfNeeded()
				return
			}

			navigator.permissions.query({ name: 'geolocation' })
				.then((status) => {
					if (status.state === 'granted') return
					if (status.state === 'denied') {
						this.locationValid = false
						this.locationError = 'Location permission is denied on this device.'
						return
					}
					this.requestLocationPermissionIfNeeded()
				})
				.catch(() => this.requestLocationPermissionIfNeeded())
		},

		requestLocationPermissionIfNeeded() {
			navigator.geolocation.getCurrentPosition(
				() => { this.locationError = null },
				(error) => {
					this.locationValid = false
					this.locationError = error?.message || 'Unable to access device location.'
				},
				{ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
			)
		},

		checkVisitLocation() {
			if (!this.shouldCheckVisitLocation() || !this.entityId) {
				this.locationChecking = false
				this.locationValid = null
				this.locationError = null
				return Promise.resolve(null)
			}

			if (!navigator.geolocation) {
				this.locationChecking = false
				this.locationValid = false
				this.locationError = 'GPS is not available on this device.'
				return Promise.resolve(null)
			}

			this.locationChecking = true
			this.locationValid = null
			this.locationError = null

			return new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					enableHighAccuracy: true,
					timeout: 10000,
					maximumAge: 0
				})
			})
				.then((position) => this.$store.dispatch('visits/checkLocation', {
					entityId: this.entityId,
					latitude: position.coords.latitude,
					longitude: position.coords.longitude
				}))
				.then((response) => {
					const isValid = Boolean(
						response?.isValid ?? response?.valid ?? response?.success ??
						response?.data?.isValid ?? response?.data?.valid ?? response?.data?.success ??
						response
					)
					this.locationValid = isValid
					this.locationError = isValid ? null : 'You are not at the customer location.'
					return response
				})
				.catch((error) => {
					this.locationValid = false
					this.locationError = error?.response?.data?.message || error?.message || 'Unable to verify your location.'
					return null
				})
				.finally(() => {
					this.locationChecking = false
				})
		},

		hydrateFromDraft() {
			const draft = this.$store.state.visitDraft

			// route query params are always strings; the store's ids are numbers
			const toId = value => {
				if (value === null || value === undefined || value === '') return null
				return Number.isNaN(Number(value)) ? value : Number(value)
			}

			this.customerTypeId = draft.customerTypeId || toId(this.$route.query.customerTypeId)
			this.entityId = draft.entityId || toId(this.$route.query.entityId)
			this.visitDate = draft.visitDate || this.$route.query.visitDate || null
			this.priceTypeId = draft.priceTypeId
			this.durationInMinutes = draft.durationInMinutes
			this.invoiceItems = draft.invoiceItems
			this.originalMaterialAmounts = draft.originalMaterialAmounts || {}
			this.returnItems = draft.returnItems
			this.orderItems = draft.orderItems
			this.orderDeliveryDate = draft.orderDeliveryDate
			this.orderDeliveryTime = draft.orderDeliveryTime
			this.orderDeliveryPlace = draft.orderDeliveryPlace
			this.visitNotes = draft.marketing.visitNotes
			this.coreMessage = draft.marketing.coreMessage
			this.centerNotes = draft.marketing.centerNotes
			this.objection = draft.marketing.objection
			this.nextCall = draft.marketing.nextCall
			this.unavailableMaterialIds = draft.marketing.unavailableMaterialIds
			this.personsNames = draft.marketing.residentDoctorNames
			this.competitors = draft.marketing.competitors
			this.otherSamples = draft.marketing.otherSamples
		},

		fetchLookups() {
			return Promise.all([
				!this.regions.length ? this.$store.dispatch('regions/fetchAll') : null,
				!this.allMaterials.length ? this.$store.dispatch('materials/fetchAll', {}) : null,
				!this.transactions.length ? this.$store.dispatch('materialsTransactions/fetchAll') : null,
				!this.priceTypes.length ? this.$store.dispatch('priceTypes/fetchAll') : null
			])
		},

		persistDraft() {
			return Promise.all([
				this.$store.dispatch('visitDraft/setPriceType', this.priceTypeId),
				this.$store.dispatch('visitDraft/setDuration', this.durationInMinutes),
				this.$store.dispatch('visitDraft/setInvoiceItems', this.invoiceItems),
				this.$store.dispatch('visitDraft/setReturnItems', this.returnItems),
				this.$store.dispatch('visitDraft/setOrderItems', this.orderItems),
				this.$store.dispatch('visitDraft/setOrderDeliveryDate', this.orderDeliveryDate),
				this.$store.dispatch('visitDraft/setOrderDeliveryTime', this.orderDeliveryTime),
				this.$store.dispatch('visitDraft/setOrderDeliveryPlace', this.orderDeliveryPlace),
				this.$store.dispatch('visitDraft/setMarketing', {
					visitNotes: this.visitNotes,
					coreMessage: this.coreMessage,
					centerNotes: this.centerNotes,
					objection: this.objection,
					nextCall: this.nextCall,
					unavailableMaterialIds: this.unavailableMaterialIds,
					residentDoctorNames: this.personsNames,
					competitors: this.competitors,
					otherSamples: this.otherSamples
				})
			])
		},

		getMissingRequiredFieldLabels() {
			const labels = []

			if (this.filledOrderItems.length > 0 && !this.orderDeliveryDate) {
				labels.push(this.$T('inputs.delivery-date'))
			}

			return labels
		},

		next() {
			const missingFieldLabels = this.getMissingRequiredFieldLabels()

			if (missingFieldLabels.length) {
				this.$refs.form.validate()
				this.activeTab = 2
				this.$eventBus.$emit(
					'show-snackbar',
					this.$T('messages.required-fields-missing-$', { fields: missingFieldLabels.join(', ') }),
					'error'
				)
				return
			}

			// only the invoice tab currently carries :rules (order's delivery-date is already
			// handled above, and it's the default active tab so its fields are always mounted)
			if (!this.$refs.form.validate()) {
				this.activeTab = 0
				return
			}

			this.persistDraft().then(() => {
				this.$router.push({ name: 'add-visit-payment' })
			})
		},

		cancel() {
			this.$store.dispatch('visitDraft/reset').then(() => {
				this.$router.push({ name: 'visits' })
			})
		},

		openChangeCustomerSheet() {
			if (this.isReadonly) return

			this.$refs.ChangeCustomerSheet.open(this.customerTypeId, this.visitDate, {
				regionId: this.entityRegionId,
				entityId: this.entityId,
				entityName: this.entityDisplayName
			})
		},

		onCustomerChanged({ customerTypeId, entityId, entityName, visitDate }) {
			this.customerTypeId = customerTypeId
			this.entityId = entityId
			this.visitDate = visitDate

			this.$store.dispatch('visitDraft/setCustomer', { customerTypeId, entityId, entityName, visitDate })
		},

		previewInvoice() {
			const visitId = this.$store.state.visitDraft.id
			if (!visitId) return

			this.$router.push({ name: 'visit-invoice-preview', params: { visitId } })
		},

		openPaymentDetails() {
			this.paymentDetailsSheet = true
		},

		viewLastVisit() {
			if (!this.entityId) return

			this.lastVisitSheet = true
			this.lastVisitLoading = true
			this.lastVisit = null

			this.$store.dispatch('visits/fetchLastCustomerVisit', { entityId: this.entityId })
				.then(visit => { this.lastVisit = visit })
				.catch(() => { this.lastVisit = null })
				.finally(() => { this.lastVisitLoading = false })
		},

		deleteVisit() {
			this.deleteLoading = true
			this.$store.dispatch('visits/delete', { id: this.$store.state.visitDraft.id })
				.then(() => this.$store.dispatch('visitDraft/reset'))
				.then(() => {
					this.confirmDeleteDialog = false
					this.$router.push({ name: 'visits' })
				})
				.finally(() => {
					this.deleteLoading = false
				})
		}
	},

	created() {
		const visitId = this.$route.query.visitId

		// viewing/editing an existing visit: hydrate the draft from the server visit first
		const ready = visitId
			? Promise.resolve(this.getVisitById(visitId) || this.$store.dispatch('visits/fetchById', { id: visitId }))
				.then(visit => this.$store.dispatch('visitDraft/loadFromVisit', visit))
			: Promise.resolve()

		ready.then(() => {
			this.hydrateFromDraft()
			this.isReadonly = Boolean(visitId) && this.$route.query.readonly === 'true'

			this.fetchLookups().then(() => {
				// TODO: replace with configuration-driven default later
				if (!this.priceTypeId) this.priceTypeId = this.priceTypes[0]?.id ?? null
			})

			this.getHoliday(this.visitDate)
			this.ensureLocationPermissionOnPageEnter()
			if (this.shouldCheckVisitLocation() && this.entityId) {
				this.checkVisitLocation()
			}
		})
	}
}
</script>

<style scoped>
.add-visit-details {
	min-height: 100%;
	background-color: #f2f7f6;
}

.add-visit-details__header {
	cursor: pointer;
	background-color: #fff;
	border-bottom: 1px solid #e0e0e0;
}

.add-visit-details__change-customer {
	cursor: pointer;
}

/* يُقصّ ما يتجاوز حواف البطاقة لأن الصورة تلامسها مباشرة */
.add-visit-details__customer-card {
	overflow: hidden;
}

/* v-img is flex: 1 1 auto by default, which would stretch it across the summary row */
.add-visit-details__entity-image {
	flex: 0 0 88px;
}

/* rounded on the card's outer corners only, square where it meets the text */
[dir=rtl] .add-visit-details__entity-image {
	border-radius: 0 8px 8px 0;
}

[dir=ltr] .add-visit-details__entity-image {
	border-radius: 8px 0 0 8px;
}

/* min-width:auto on a flex item keeps it at its content width, which would push the photo off the card */
.add-visit-details__customer-info {
	min-width: 0;
}

.add-visit-details__customer-name {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.add-visit-details__tabs {
	overflow-x: auto;
	align-items: stretch;
	border-bottom: 1px solid #e0e0e0;
}

.add-visit-details__tab {
	display: flex;
	align-items: center;
	align-self: stretch;
	gap: 6px;
	flex: none;
	padding: 8px 16px;
	font-size: 0.8125rem;
	background-color: #fff;
	border: 1px solid #e0e0e0;
	border-bottom: none;
	border-radius: 12px 12px 0 0;
}

/* the first tab sits flush against the screen edge, so that corner stays square */
[dir=rtl] .add-visit-details__tab:first-child {
	border-radius: 12px 0 0 0;
	border-right: none;
}

[dir=ltr] .add-visit-details__tab:first-child {
	border-radius: 0 12px 0 0;
	border-left: none;
}

.add-visit-details__tab--active {
	border-color: transparent;
}

.add-visit-details__panel {
	background-color: #fff;
}

.add-visit-details__tab-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 20px;
	height: 20px;
	padding: 0 5px;
	border-radius: 10px;
	font-size: 0.75rem;
}

.add-visit-details__actions {
	gap: 12px;
}

.add-visit-details__primary-btn,
.add-visit-details__secondary-btn {
	flex: 1;
}

.add-visit-details__footer {
	border-top: 1px solid #e0e0e0;
}
</style>
